import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

const SUN = ["Full Sun", "Partial Sun", "Full Shade"];
const SEASON = ["Spring", "Spring–Summer", "Summer", "Late Spring–Summer", "Spring & Fall", "Fall", "Perennial"];
const CAT = ["Vegetable", "Herb", "Flower", "Fruit", "Custom"];

export default function AddCustomPlantDialog({ onCreated }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    name: "", category: "Custom", sun: "Full Sun", water: "Moderate",
    season: "Spring–Summer", days_to_harvest: 60, spacing_in: 12, description: "", notes: "",
  });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return toast.error("Give your plant a name.");
    setBusy(true);
    try {
      const r = await api.post("/garden", form);
      onCreated?.(r.data);
      toast.success(`${form.name} added to your garden.`);
      setOpen(false);
      setForm({ ...form, name: "", description: "", notes: "" });
    } catch (e) {
      toast.error("Could not add plant.");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" className="gap-2 border-accent/50 text-accent-foreground" data-testid="add-custom-plant-btn">
          <Plus className="w-4 h-4 text-accent" /> Add custom plant
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-card max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl">A new resident</DialogTitle>
          <DialogDescription>Add a plant the library doesn't yet know.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <Field label="Name">
            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="custom-name" />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Category">
              <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                <SelectTrigger data-testid="custom-category"><SelectValue /></SelectTrigger>
                <SelectContent>{CAT.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="Sun">
              <Select value={form.sun} onValueChange={(v) => setForm({ ...form, sun: v })}>
                <SelectTrigger data-testid="custom-sun"><SelectValue /></SelectTrigger>
                <SelectContent>{SUN.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Water">
              <Input value={form.water} onChange={(e) => setForm({ ...form, water: e.target.value })} data-testid="custom-water" />
            </Field>
            <Field label="Season">
              <Select value={form.season} onValueChange={(v) => setForm({ ...form, season: v })}>
                <SelectTrigger data-testid="custom-season"><SelectValue /></SelectTrigger>
                <SelectContent>{SEASON.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
          </div>
          <Field label="Description">
            <Textarea rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="custom-description" />
          </Field>
          <Field label="My notes (optional)">
            <Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="custom-notes" />
          </Field>
          <DialogFooter>
            <Button type="submit" disabled={busy} className="bg-primary hover:bg-primary/90" data-testid="save-custom-plant">
              {busy ? "Planting…" : "Plant it"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, children }) {
  return (
    <div className="space-y-1">
      <Label className="label-overline">{label}</Label>
      {children}
    </div>
  );
}
